import React, { useState } from "react"
import { isEmpty } from "lodash"

import { toast } from "@kit/design/toast"

import { useCurrentUser } from "hooks/use-current-user"
import { useStaticFeature } from "hooks/use-feature"
import { getOptInEligibleStates } from "pages/signals/constants"

import { Celebration } from "./celebration"
import { PlanComparisonContent } from "./plan-comparison-content"
import { UnauthorizedUpgrade } from "./unauthorized-upgrade"

import { PlanComparisonProvider, usePlanComparisonContext } from "./context"
import { AddCardModal } from "../add-card-modal/add-card-modal"
import { PlanSwitchConfirmationModal } from "./plan-switch-confirmation-modal"
import { SignalsOptInModal } from "../signals-opt-in/signals-opt-in-modal"

const PlanComparisonInner = ({
  title,
  description,
  afterCelebration = () => {},
  celebrationMessage = ""
}) => {
  const {
    activeModal,
    closeAllModals,
    currentPlan,
    hasAnyPromoApplied,
    plans,
    plansForPaymentForm,
    refreshAllowedActivities,
    setActiveModal,
    setSignalsOptedIn,
    signalsOptedIn,
    switchPlan,
    toPlan,
    MODALS
  } = usePlanComparisonContext()

  const { refreshSignalsAccessLevel } = useCurrentUser()
  const signalsExpansionEnabled = useStaticFeature(
    "signals_in_app_expansion"
  ).enabled
  const endTrialEarlyEnabled = useStaticFeature(
    "signals_end_trial_early"
  ).enabled

  const [planSwitchInProgress, setPlanSwitchInProgress] = useState(false)

  if (isEmpty(plans)) return null

  const goToCelebration = optedIn => {
    setActiveModal(MODALS.CELEBRATION, { signals_opted_in: optedIn })
  }

  const canOptIntoSignals = async () => {
    if (!signalsExpansionEnabled || toPlan.service_level !== "creator_pro") {
      return false
    }

    try {
      const { state } = await refreshSignalsAccessLevel()
      return getOptInEligibleStates(endTrialEarlyEnabled).includes(state)
    } catch (error) {
      window.SentryClient?.captureException(error)
      return false
    }
  }

  const goToPostUpgradeStep = async () => {
    if (await canOptIntoSignals()) {
      setActiveModal(MODALS.SIGNALS_OPT_IN)
    } else {
      goToCelebration(false)
    }
  }

  const handlePlanSwitch = async () => {
    setPlanSwitchInProgress(true)

    try {
      await switchPlan({
        planId: toPlan.id,
        promoCode: toPlan.promo_code?.redemption_code
      })
      await goToPostUpgradeStep()
    } catch (error) {
      toast({
        type: "error",
        text: "Sorry, we couldn't change your plan. If this error persists, please contact support."
      })
      closeAllModals()
    }

    setPlanSwitchInProgress(false)
  }

  const closeCelebration = () => {
    refreshAllowedActivities()
    closeAllModals()
    afterCelebration()
  }

  return (
    <>
      {activeModal === MODALS.UNAUTHORIZED && (
        <UnauthorizedUpgrade onClose={closeAllModals} />
      )}

      {activeModal === MODALS.COMPARISON && (
        <PlanComparisonContent title={title} description={description} />
      )}

      {activeModal === MODALS.PLAN_SWITCH_CONFIRMATION && (
        <PlanSwitchConfirmationModal
          label="Your new plan"
          onClose={closeAllModals}
          fromPlan={currentPlan}
          toPlan={toPlan}
          onConfirm={handlePlanSwitch}
          onCancel={closeAllModals}
          disableButton={planSwitchInProgress}
          tracking={{
            location: "plan_modal_comparison",
            monthly_difference_displayed:
              Boolean(toPlan.monthly_difference) && !hasAnyPromoApplied
          }}
        />
      )}

      {activeModal === MODALS.PAYMENT && (
        <AddCardModal
          title={`Upgrade to ${toPlan.display_name}`}
          onClose={closeAllModals}
          paymentFormOptions={{
            handleSubscription: goToPostUpgradeStep,
            plans: plansForPaymentForm,
            modalOptions: { handleClose: closeAllModals }
          }}
          tracking={{
            location: "plan_modal_comparison",
            plan: toPlan.service_level
          }}
        />
      )}

      {activeModal === MODALS.SIGNALS_OPT_IN && (
        <SignalsOptInModal
          isPostUpgrade={true}
          onOptInSuccess={() => {
            setSignalsOptedIn(true)
            goToCelebration(true)
          }}
          onSkip={() => goToCelebration(false)}
          onClose={() => goToCelebration(false)}
        />
      )}

      {activeModal === MODALS.CELEBRATION && (
        <Celebration
          afterCelebration={closeCelebration}
          message={celebrationMessage}
          {...(signalsOptedIn
            ? {
                subtitleSuffix:
                  "and you're in the queue to get Subscriber Signals.",
                message: "We'll email you as soon as your data starts syncing."
              }
            : {})}
        />
      )}
    </>
  )
}

export const PlanComparison = props => {
  return (
    <PlanComparisonProvider {...props}>
      <PlanComparisonInner {...props} />
    </PlanComparisonProvider>
  )
}
